import { collection, doc, getDoc, getDocs, increment, setDoc, updateDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from './firebase';
import { adminBillingService } from './adminBilling';
import { getWorkspaceCaseCount } from './docxCaseImport';
import {
  accountService,
  estimateWorkspaceStorageBytes,
  mbToBytes,
} from './account';
import {
  DEFAULT_ENTITLEMENTS,
  formatPlanLabel,
  normalizeLegacyPlanId,
  normalizePlanDefinition,
  type LimitValue,
  type PlanDefinition,
  type PlanEntitlements,
  type TemplateAccessTier,
  type BuiltInPlanId,
} from './billing/types';
import { isPlatformAdmin } from './adminAccess';

export type UsagePeriod = {
  period: string;
  aiDrafts: number;
  documentsCreated: number;
};

export type FeatureCheckResult = {
  allowed: boolean;
  reason?: string;
  limit?: LimitValue;
  used?: number;
  planId?: string;
  upgradePlan?: string;
};

export type WorkspaceCaseLimitResult = {
  allowed: boolean;
  count: number;
  limit: LimitValue;
  planId?: string;
  upgradePlan?: string;
  message?: string;
};

export type StorageLimitResult = {
  allowed: boolean;
  usedBytes: number;
  limitMb: LimitValue;
  planId?: string;
  upgradePlan?: string;
  message?: string;
};

export type SeatLimitResult = {
  allowed: boolean;
  used: number;
  limit: LimitValue;
  planId?: string;
  upgradePlan?: string;
  message?: string;
};

export function isCaseLimitReached(count: number, limit: LimitValue): boolean {
  return count >= limitToNumber(limit);
}

function currentPeriod() {
  return format(new Date(), 'yyyy-MM');
}

export function limitToNumber(value: LimitValue): number {
  return value === 'unlimited' ? Number.POSITIVE_INFINITY : value;
}

export function formatLimit(value: LimitValue): string {
  return value === 'unlimited' ? 'Unlimited' : String(value);
}

export function resolveUserPlanId(userData: Record<string, unknown> | null | undefined): string {
  if (!userData) return 'free';
  const accountPlan = userData._accountPlanId as string | undefined;
  if (accountPlan) return normalizeLegacyPlanId(accountPlan);
  const sub = userData.subscription as Record<string, unknown> | undefined;
  const raw = String(userData.planTier ?? sub?.plan ?? 'free').toLowerCase();
  return normalizeLegacyPlanId(raw);
}

export async function resolvePlanIdForUser(
  userId: string,
  userData: Record<string, unknown> | null | undefined,
): Promise<string> {
  const account = await accountService.getAccountForUser(userId);
  if (account) return accountService.resolveAccountPlanId(account);
  return resolveUserPlanId(userData);
}

export async function loadPlanCatalog(): Promise<PlanDefinition[]> {
  const plans = await adminBillingService.getPlanCatalog();
  return plans.map((p) => normalizePlanDefinition(p as PlanDefinition));
}

export async function getPlanEntitlements(planId: string): Promise<PlanEntitlements> {
  const catalog = await loadPlanCatalog();
  const normalized = normalizeLegacyPlanId(planId);
  const plan = catalog.find((p) => p.id === normalized && p.enabled !== false);
  if (plan) return plan.entitlements;
  const builtIn = DEFAULT_ENTITLEMENTS[normalized as BuiltInPlanId];
  return builtIn ?? DEFAULT_ENTITLEMENTS.free;
}

export async function getEntitlementsForUser(
  userId: string,
  userData: Record<string, unknown> | null | undefined,
): Promise<{ planId: string; entitlements: PlanEntitlements }> {
  const account = await accountService.getAccountForUser(userId);
  if (account) {
    const planId = accountService.resolveAccountPlanId(account);
    const entitlements = await accountService.getAccountEntitlements(account);
    return { planId, entitlements };
  }
  const planId = resolveUserPlanId(userData);
  const entitlements = await getPlanEntitlements(planId);
  return { planId, entitlements };
}

function suggestUpgrade(planId: string): string | undefined {
  if (planId === 'free') return 'pro';
  if (planId === 'pro') return 'chamber';
  if (planId === 'chamber') return 'enterprise';
  return undefined;
}

export async function checkWorkspaceCaseLimit(
  workspaceId: string,
  auth?: { isAdmin?: boolean; email?: string | null; role?: string | null },
): Promise<WorkspaceCaseLimitResult> {
  const count = await getWorkspaceCaseCount(workspaceId);

  if (auth?.isAdmin || isPlatformAdmin(auth?.email, auth?.role)) {
    return { allowed: true, count, limit: 'unlimited', planId: 'admin' };
  }

  const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
  const ownerId = wsSnap.exists()
    ? (wsSnap.data().ownerId as string | undefined) ?? workspaceId
    : workspaceId;

  const ownerSnap = await getDoc(doc(db, 'users', ownerId));
  const ownerData = ownerSnap.exists() ? ownerSnap.data() : null;
  const { planId, entitlements } = await getEntitlementsForUser(
    ownerId,
    ownerData as Record<string, unknown>,
  );
  const limit = entitlements.casesPerWorkspace;

  if (isCaseLimitReached(count, limit)) {
    return {
      allowed: false,
      count,
      limit,
      planId,
      upgradePlan: suggestUpgrade(planId),
      message: `Case limit reached (${count}/${formatLimit(limit)}). Your ${formatPlanLabel(planId)} plan allows up to ${formatLimit(limit)} cases per workspace.`,
    };
  }

  return { allowed: true, count, limit, planId };
}

export async function checkStorageLimit(
  workspaceId: string,
  auth?: { isAdmin?: boolean; email?: string | null; role?: string | null },
): Promise<StorageLimitResult> {
  if (auth?.isAdmin || isPlatformAdmin(auth?.email, auth?.role)) {
    return { allowed: true, usedBytes: 0, limitMb: 'unlimited', planId: 'admin' };
  }

  const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
  const ownerId = wsSnap.exists()
    ? (wsSnap.data().ownerId as string | undefined) ?? workspaceId
    : workspaceId;
  const ownerSnap = await getDoc(doc(db, 'users', ownerId));
  const { planId, entitlements } = await getEntitlementsForUser(
    ownerId,
    ownerSnap.exists() ? (ownerSnap.data() as Record<string, unknown>) : null,
  );

  const account = await accountService.getAccountForUser(ownerId);
  if (account?.storageEnforcementEnabled === false) {
    return { allowed: true, usedBytes: 0, limitMb: 'unlimited', planId };
  }

  let globalEnforcement = true;
  try {
    const configSnap = await getDoc(doc(db, 'system', 'config'));
    if (configSnap.exists()) {
      const cfg = configSnap.data();
      if (cfg.storageEnforcementEnabled === false) {
        globalEnforcement = false;
      }
    }
  } catch {
    /* ignore */
  }
  if (!globalEnforcement) {
    const limitMb = account?.grandfatherStorageMb ?? entitlements.storageMb;
    return { allowed: true, usedBytes: 0, limitMb, planId };
  }

  const limitMb =
    account?.grandfatherStorageMb ??
    entitlements.storageMb;
  const usedBytes = await estimateWorkspaceStorageBytes(workspaceId);
  const limitBytes = limitToNumber(limitMb) === Number.POSITIVE_INFINITY
    ? Number.POSITIVE_INFINITY
    : mbToBytes(limitMb as number);

  if (usedBytes >= limitBytes) {
    return {
      allowed: false,
      usedBytes,
      limitMb,
      planId,
      upgradePlan: suggestUpgrade(planId),
      message: `Storage limit reached. Your ${formatPlanLabel(planId)} plan includes ${formatLimit(limitMb)} storage.`,
    };
  }

  return { allowed: true, usedBytes, limitMb, planId };
}

export async function checkSeatLimit(
  accountId: string,
  auth?: { isAdmin?: boolean; email?: string | null; role?: string | null },
): Promise<SeatLimitResult> {
  if (auth?.isAdmin || isPlatformAdmin(auth?.email, auth?.role)) {
    return { allowed: true, used: 0, limit: 'unlimited', planId: 'admin' };
  }

  const account = await accountService.getAccountById(accountId);
  if (!account) {
    return { allowed: true, used: 1, limit: 1, planId: 'free' };
  }

  const planId = accountService.resolveAccountPlanId(account);
  const entitlements = await accountService.getAccountEntitlements(account);
  const members = await accountService.listAccountMembers(accountId);
  const used = members.length;
  const limit = entitlements.maxUsers;

  if (used >= limitToNumber(limit)) {
    return {
      allowed: false,
      used,
      limit,
      planId,
      upgradePlan: suggestUpgrade(planId),
      message: `Seat limit reached (${used}/${formatLimit(limit)}). Upgrade to add more team members.`,
    };
  }

  return { allowed: true, used, limit, planId };
}

export async function checkWorkspaceLimit(
  accountId: string,
  auth?: { isAdmin?: boolean; email?: string | null; role?: string | null },
): Promise<FeatureCheckResult> {
  if (auth?.isAdmin || isPlatformAdmin(auth?.email, auth?.role)) {
    return { allowed: true, planId: 'admin' };
  }

  const account = await accountService.getAccountById(accountId);
  if (!account) return { allowed: true, planId: 'free' };

  const planId = accountService.resolveAccountPlanId(account);
  const entitlements = await accountService.getAccountEntitlements(account);
  const used = account.workspaceIds?.length ?? 0;
  const limit = entitlements.maxWorkspaces;

  if (used >= limitToNumber(limit)) {
    return {
      allowed: false,
      planId,
      limit,
      used,
      upgradePlan: suggestUpgrade(planId),
      reason: `Workspace limit reached (${used}/${formatLimit(limit)}). Upgrade for more organizations.`,
    };
  }

  return { allowed: true, planId, limit, used };
}

export async function getUserUsage(userId: string): Promise<UsagePeriod> {
  const period = currentPeriod();
  try {
    const account = await accountService.getAccountForUser(userId);
    if (account?.usage?.period === period) {
      return {
        period,
        aiDrafts: account.usage.aiDrafts,
        documentsCreated: account.usage.documentsCreated,
      };
    }
    const snap = await getDoc(doc(db, 'users', userId));
    if (!snap.exists()) {
      return { period, aiDrafts: 0, documentsCreated: 0 };
    }
    const usage = snap.data().usagePeriod as UsagePeriod | undefined;
    if (!usage || usage.period !== period) {
      return { period, aiDrafts: 0, documentsCreated: 0 };
    }
    return {
      period,
      aiDrafts: Number(usage.aiDrafts ?? 0),
      documentsCreated: Number(usage.documentsCreated ?? 0),
    };
  } catch {
    return { period, aiDrafts: 0, documentsCreated: 0 };
  }
}

async function ensureUsageDoc(userId: string): Promise<UsagePeriod> {
  const period = currentPeriod();
  const usage = await getUserUsage(userId);
  if (usage.period === period) return usage;
  const fresh = { period, aiDrafts: 0, documentsCreated: 0 };
  await setDoc(doc(db, 'users', userId), { usagePeriod: fresh }, { merge: true });
  return fresh;
}

export type GatingGate =
  | 'aiDraft'
  | 'document'
  | 'lexCounsel'
  | 'templates'
  | 'customTemplates'
  | 'team'
  | 'analytics'
  | 'storage'
  | 'seat'
  | 'workspace'
  | 'workflowAutomation'
  | 'advancedPermissions'
  | 'apiAccess';

export type GatingGateRequest = {
  gate: GatingGate;
  options?: { templateTier?: TemplateAccessTier };
};

function entitlementsForPlan(
  planId: string,
  catalog?: PlanDefinition[],
): PlanEntitlements {
  const normalized = normalizeLegacyPlanId(planId);
  if (catalog?.length) {
    const plan = catalog.find((p) => p.id === normalized && p.enabled !== false);
    if (plan) return plan.entitlements;
  }
  const builtIn = DEFAULT_ENTITLEMENTS[normalized as BuiltInPlanId];
  return builtIn ?? DEFAULT_ENTITLEMENTS.free;
}

function evaluateGate(
  planId: string,
  entitlements: PlanEntitlements,
  usage: UsagePeriod,
  gate: GatingGate,
  options?: { templateTier?: TemplateAccessTier },
): FeatureCheckResult {
  switch (gate) {
    case 'lexCounsel':
      if (!entitlements.lexCounselEnabled) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'pro',
          reason: 'LexCounsel AI is available on Pro, Chamber, and Enterprise plans.',
        };
      }
      return { allowed: true, planId };

    case 'aiDraft': {
      const limit = entitlements.aiDraftsPerMonth;
      const used = usage.aiDrafts;
      if (used >= limitToNumber(limit)) {
        return {
          allowed: false,
          planId,
          limit,
          used,
          upgradePlan: suggestUpgrade(planId),
          reason: `You've used ${used} of ${formatLimit(limit)} AI drafts this month. Upgrade for more.`,
        };
      }
      return { allowed: true, planId, limit, used };
    }

    case 'document': {
      const limit = entitlements.documentsPerMonth;
      const used = usage.documentsCreated;
      if (used >= limitToNumber(limit)) {
        return {
          allowed: false,
          planId,
          limit,
          used,
          upgradePlan: suggestUpgrade(planId),
          reason: `You've reached your ${formatLimit(limit)} document limit for this month.`,
        };
      }
      return { allowed: true, planId, limit, used };
    }

    case 'templates': {
      const required = options?.templateTier ?? 'standard';
      const tiers: TemplateAccessTier[] = ['basic', 'standard', 'full'];
      const userTier = entitlements.templatesAccess;
      if (tiers.indexOf(userTier) < tiers.indexOf(required)) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'pro',
          reason: `This template requires ${required} plan access. Your plan includes ${userTier} templates.`,
        };
      }
      return { allowed: true, planId };
    }

    case 'customTemplates':
      // Workspace "My Templates" are available on all plans; this gate is reserved
      // for future firm-wide custom template features.
      if (!entitlements.customTemplates) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'chamber',
          reason: 'Custom templates are available on Chamber and Enterprise.',
        };
      }
      return { allowed: true, planId };

    case 'team':
      if (!entitlements.teamWorkspaces) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'chamber',
          reason: 'Team workspaces require Chamber or Enterprise.',
        };
      }
      return { allowed: true, planId };

    case 'workflowAutomation':
      if (!entitlements.workflowAutomation) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'chamber',
          reason: 'Workflow automation is available on Chamber and Enterprise.',
        };
      }
      return { allowed: true, planId };

    case 'advancedPermissions':
      if (!entitlements.advancedPermissions) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'chamber',
          reason: 'Advanced permissions require Chamber or Enterprise.',
        };
      }
      return { allowed: true, planId };

    case 'apiAccess':
      if (!entitlements.apiAccess) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'enterprise',
          reason: 'API access is available on Enterprise.',
        };
      }
      return { allowed: true, planId };

    case 'analytics':
      if (!entitlements.advancedAnalytics) {
        return {
          allowed: false,
          planId,
          upgradePlan: 'pro',
          reason: 'Advanced analytics are available on Pro and above.',
        };
      }
      return { allowed: true, planId };

    case 'storage':
    case 'seat':
    case 'workspace':
      return { allowed: true, planId };

    default:
      return { allowed: true, planId };
  }
}

export const featureGatingService = {
  resolveUserPlanId,
  resolvePlanIdForUser,
  loadPlanCatalog,
  getPlanEntitlements,
  getEntitlementsForUser,
  checkWorkspaceCaseLimit,
  checkStorageLimit,
  checkSeatLimit,
  checkWorkspaceLimit,

  async check(
    userId: string,
    userData: Record<string, unknown> | null | undefined,
    check: {
      isAdmin?: boolean;
      email?: string | null;
      role?: string | null;
    },
    gate: GatingGate,
    options?: { templateTier?: TemplateAccessTier },
    catalog?: PlanDefinition[],
  ): Promise<FeatureCheckResult> {
    if (check.isAdmin || isPlatformAdmin(check.email, check.role)) {
      return { allowed: true, planId: 'admin' };
    }

    const { planId, entitlements } = catalog?.length
      ? {
          planId: resolveUserPlanId(userData),
          entitlements: entitlementsForPlan(resolveUserPlanId(userData), catalog),
        }
      : await getEntitlementsForUser(userId, userData);
    const usage = await getUserUsage(userId);
    return evaluateGate(planId, entitlements, usage, gate, options);
  },

  async checkGates(
    userId: string,
    userData: Record<string, unknown> | null | undefined,
    auth: {
      isAdmin?: boolean;
      email?: string | null;
      role?: string | null;
    },
    requests: GatingGateRequest[],
    catalog?: PlanDefinition[],
  ): Promise<FeatureCheckResult[]> {
    if (auth.isAdmin || isPlatformAdmin(auth.email, auth.role)) {
      return requests.map(() => ({ allowed: true, planId: 'admin' }));
    }

    const { planId, entitlements } = catalog?.length
      ? {
          planId: resolveUserPlanId(userData),
          entitlements: entitlementsForPlan(resolveUserPlanId(userData), catalog),
        }
      : await getEntitlementsForUser(userId, userData);
    const usage = await getUserUsage(userId);

    return requests.map(({ gate, options }) =>
      evaluateGate(planId, entitlements, usage, gate, options),
    );
  },

  async incrementUsage(
    userId: string,
    field: 'aiDrafts' | 'documentsCreated',
    amount = 1,
  ): Promise<void> {
    await ensureUsageDoc(userId);
    const period = currentPeriod();
    const key = field === 'aiDrafts' ? 'usagePeriod.aiDrafts' : 'usagePeriod.documentsCreated';
    await updateDoc(doc(db, 'users', userId), {
      [key]: increment(amount),
      'usagePeriod.period': period,
    });

    const account = await accountService.getAccountForUser(userId);
    if (account) {
      const usageKey = field === 'aiDrafts' ? 'usage.aiDrafts' : 'usage.documentsCreated';
      await updateDoc(doc(db, 'accounts', account.id), {
        [usageKey]: increment(amount),
        'usage.period': period,
        updatedAt: new Date().toISOString(),
      });
    }
  },
};
